import React from 'react';
import { useIntl } from 'react-intl';
import { MUSIC_VIDEO_SIZE_PX } from 'fym-shared/src/music24/musicVideoLook';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { planNameMessages } from '../../music24/config/music24PlanNames';
import { useVideoCredits } from '../../music24/promote/credits/useVideoCredits';
import type { CutSaveState } from '../doc/useCutAutosave';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { editorMessages } from '../messages/editorMessages';
import { formatTime } from '../timeline/timelineGeometry';
import { CutMenu } from './CutMenu';
import { SaveState } from './SaveState';
import { UndoRedo } from './UndoRedo';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface EditorTopBarProps {
  saveState: CutSaveState;
  onBack: () => void;
  onWatch: () => void;
  onMake: () => void;
  isMaking: boolean;
  onNewCut: () => void;
  onCopyCut: () => void;
  isCutBusy: boolean;
}

const bold = (chunks: React.ReactNode[]) => <b key="b">{chunks}</b>;

/**
 * The top bar (design b1): back, the song, Undo and Redo, the cut menu, the save state, the plan,
 * the credits, "Watch full screen" and "Make the video · 3 credits" (with a lock below Pro, e4).
 */
const EditorTopBarComponent: React.FC<EditorTopBarProps> = (props) => {
  const { saveState, onBack, onWatch, onMake, isMaking, onNewCut, onCopyCut, isCutBusy } = props;
  const intl = useIntl();
  const { data, doc, readOnly, accountId } = useMusicVideoEditor();
  const { credits } = useVideoCredits({ accountId });
  const { song, plan } = data;
  const size = MUSIC_VIDEO_SIZE_PX;

  return (
    <header className={styles.top}>
      <button type="button" className={styles.back} onClick={onBack}>
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M15 5l-7 7 7 7" />
        </svg>
        {intl.formatMessage(editorMessages.back)}
      </button>
      <div className={styles.song}>
        {song.artworkUrl !== null && <img src={song.artworkUrl} alt="" />}
        <div className={styles.songText}>
          <b>{song.title}</b>
          <small>
            {intl.formatMessage(editorMessages.songLine, {
              artist: song.artistName,
              length: formatTime(doc.settings.endSec - doc.settings.startSec),
              size: `${size.width} × ${size.height}`,
            })}
          </small>
        </div>
      </div>
      <span className={styles.topSeparator} />
      <UndoRedo />
      <CutMenu onNewCut={onNewCut} onCopyCut={onCopyCut} isBusy={isCutBusy} />
      <SaveState state={saveState} readOnly={readOnly} />
      <span className={styles.grow} />
      {credits !== null && (
        <span className={styles.planChip}>
          {intl.formatMessage(editorMessages.planChip, { plan: intl.formatMessage(planNameMessages[credits.plan]) })}
        </span>
      )}
      <span className={styles.credits}>
        {plan.creditsLeft === null
          ? intl.formatMessage(editorMessages.creditsNoLimit, { b: bold })
          : intl.formatMessage(editorMessages.credits, { b: bold, count: plan.creditsLeft })}
      </span>
      <SMLButton size="sm" outline omitCap onClick={onWatch}>
        {intl.formatMessage(editorMessages.watch)}
      </SMLButton>
      <SMLButton
        size="sm"
        omitCap
        isLoading={isMaking}
        disabled={isMaking || readOnly || doc.clips.length === 0}
        hoverTooltip={plan.canRender ? undefined : intl.formatMessage(editorMessages.lockedLabel)}
        onClick={onMake}
      >
        {plan.canRender ? (
          intl.formatMessage(editorMessages.make, { credits: plan.renderCredits })
        ) : (
          <>
            <svg
              className={styles.lockMark}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>{' '}
            {intl.formatMessage(editorMessages.makeLocked)}
          </>
        )}
      </SMLButton>
    </header>
  );
};

export const EditorTopBar = TypedMemo(EditorTopBarComponent);
